import "./ResightingAssignments.css";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppContext from "../../Context/AppContext";
import {
  formatDisplayDate,
  formatQcStatus,
} from "./recordDetailsUtils";

function ResightingSupervisorDashboard() {
  const BASE_URL_LOC = process.env.REACT_APP_SERVER_LOCATION;
  const appStates = useContext(AppContext);
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const fetchResearcherSubmissions = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const authToken = appStates.getToken();
      if (!authToken) {
        setSubmissions([]);
        setErrorMessage("Session expired. Please log in again.");
        return;
      }

      const response = await fetch(
        `${BASE_URL_LOC}/api/resighting/researcher/submissions`,
        {
          method: "GET",
          redirect: "follow",
          headers: {
            "authentication-token": authToken,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Headers": "authentication-token",
            "Access-Control-Expose-Headers": "authentication-token",
          },
          mode: "cors",
        }
      );

      const body = await response.json();
      if (!response.ok) {
        setSubmissions([]);
        setErrorMessage(body?.error || "Unable to load submissions right now.");
        return;
      }

      setSubmissions(Array.isArray(body?.data) ? body.data : []);
    } catch (error) {
      setSubmissions([]);
      setErrorMessage("Unable to load submissions right now.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchResearcherSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInspect = useCallback(
    (submission) => {
      const submissionId = submission?.resighting_id ?? submission?.id;
      if (!submissionId) {
        return;
      }

      window.open(`/home/resighting/supervisor/record/${submissionId}`, '_blank', 'noopener, noreferrer');
    },
    [navigate]
  );

  const submissionColumns = useMemo(
    () => [
      {
        headerName: "ID",
        valueGetter: (params) => params.data?.resighting_id ?? params.data?.id ?? "—",
        sortable: true,
        filter: true,
        flex: 0.8,
        minWidth: 90,
      },
      {
        headerName: "RING NUMBER",
        valueGetter: (params) => params.data?.ring_number || "-",
        sortable: true,
        filter: true,
        flex: 0.8,
        minWidth: 150,
      },
      {
        headerName: "SPECIES",
        valueGetter: (params) => params.data?.scientific_name || "—",
        sortable: true,
        filter: true,
        flex: 1.25,
        minWidth: 180,
      },
      {
        headerName: "INTERN",
        valueGetter: (params) => params.data?.email || "—",
        sortable: true,
        filter: true,
        flex: 1.2,
        minWidth: 220,
      },
      {
        headerName: "STATUS",
        valueGetter: (params) => formatQcStatus(params.data?.qc_status),
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 140,
      },
      {
        headerName: "SUBMITTED",
        valueGetter: (params) =>
          formatDisplayDate(params.data?.qc_submitted_at, {
            month: "short",
            day: "2-digit",
          }),
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 100,
      },
      {
        headerName: "ACTION",
        field: "action",
        sortable: false,
        filter: false,
        minWidth: 100,
        maxWidth: 120,
        cellRenderer: (params) => (
          <button
            type="button"
            className="resighting-qc-inprogress-resume-btn"
            onClick={() => handleInspect(params.data)}
          >
            Inspect
          </button>
        ),
      },
    ],
    [handleInspect]
  );

  return (
    <div className="resighting-qc-page">
      <h2 className="resighting-qc-section-title">Submitted records</h2>
      <p className="resighting-admin-paragraph">
        Records marked <i>Copied to Avifauna</i> or <i>Duplicate</i> by interns.
        Blocked records are routed to the admin instead.
      </p>

      <div className="resighting-admin-table-wrap">
        {isLoading && (
          <p className="resighting-admin-grid-status">Loading submissions...</p>
        )}
        {!isLoading && errorMessage && (
          <p className="resighting-qc-muted">{errorMessage}</p>
        )}
        {!isLoading && !errorMessage && submissions.length === 0 && (
          <p className="resighting-admin-grid-status">
            No submissions waiting for review.
          </p>
        )}
        {!isLoading && !errorMessage && submissions.length > 0 && (
          <div className="ag-theme-quartz resighting-admin-ag-grid resighting-supervisor-ag-grid">
            <AgGridReact
              rowData={submissions}
              getRowId={(params) =>
                String(params.data?.resighting_id ?? params.data?.id ?? "")
              }
              columnDefs={submissionColumns}
              pagination={false}
              domLayout="normal"
              rowHeight={44}
              headerHeight={40}
              defaultColDef={{
                resizable: true,
                editable: false,
                sortable: true,
                filter: true,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ResightingSupervisorDashboard;
